import { createContext, useContext, useState, ReactNode } from 'react'

interface AccessibilitySettings {
  fontSize: 'small' | 'medium' | 'large' | 'extra-large'
  contrast: 'normal' | 'high'
  animationsEnabled: boolean
}

interface AccessibilityContextType {
  settings: AccessibilitySettings
  updateFontSize: (size: AccessibilitySettings['fontSize']) => void
  updateContrast: (contrast: AccessibilitySettings['contrast']) => void
  toggleAnimations: () => void
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined)

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext)
  if (!context) {
    throw new Error('useAccessibility must be used within AccessibilityProvider')
  }
  return context
}

interface AccessibilityProviderProps {
  children: ReactNode
}

export const AccessibilityProvider = ({ children }: AccessibilityProviderProps) => {
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    const saved = localStorage.getItem('accessibility-settings')
    return saved ? JSON.parse(saved) : {
      fontSize: 'medium',
      contrast: 'normal',
      animationsEnabled: true
    }
  })

  const updateSettings = (newSettings: AccessibilitySettings) => {
    setSettings(newSettings)
    localStorage.setItem('accessibility-settings', JSON.stringify(newSettings))
  }

  const updateFontSize = (fontSize: AccessibilitySettings['fontSize']) => {
    updateSettings({ ...settings, fontSize })
  }

  const updateContrast = (contrast: AccessibilitySettings['contrast']) => {
    updateSettings({ ...settings, contrast })
  }

  const toggleAnimations = () => {
    updateSettings({ ...settings, animationsEnabled: !settings.animationsEnabled })
  }

  return (
    <AccessibilityContext.Provider value={{
      settings,
      updateFontSize,
      updateContrast,
      toggleAnimations
    }}>
      <div 
        className={`
          ${settings.fontSize === 'small' ? 'text-sm' : ''}
          ${settings.fontSize === 'medium' ? 'text-base' : ''}
          ${settings.fontSize === 'large' ? 'text-lg' : ''}
          ${settings.fontSize === 'extra-large' ? 'text-xl' : ''}
          ${settings.contrast === 'high' ? 'high-contrast' : ''}
          ${!settings.animationsEnabled ? 'no-animations' : ''}
        `}
      >
        {children}
      </div>
    </AccessibilityContext.Provider>
  )
}